const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const SensorPeripheral = require('../common/sensor-peripheral');
const { default: bleUtils } = require('../../util/ble');
const { BLE_CONFIG } = require('../../util/bleCharac');

/**
 * 智能开关模块
 * 
 * @readonly
 */
const DEVICE_OPT = {
    type: 'sensor'
};

/**
 * Manage communication with a YM-SWUSB peripheral over a Scratch Link client socket.
 */
class YMSWUSB extends SensorPeripheral {
    /**
     * Construct a YM-SWUSB communication object.
     * @param {Runtime} runtime - the Scratch runtime
     * @param {string} deviceId - the id of the extension
     * @param {string} originalDeviceId - the original id of the peripheral
     */
    constructor(runtime, deviceId, originalDeviceId) {
        super(runtime, deviceId, originalDeviceId, null, BLE_CONFIG, DEVICE_OPT);
    }
}

class ScratchYMSWUSBDevice {

    /**
     * @return {string} - the ID of this device.
     */
    get DEVICE_ID() {
        return 'YMSWUSB';
    }

    /**
     * Construct a set of YM-SWUSB blocks.
     * @param {Runtime} runtime - the Scratch runtime.
     * @param {string} originalDeviceId - the original id of the peripheral
     */
    constructor(runtime, originalDeviceId) {
        this.runtime = runtime;
        this._peripheral = new YMSWUSB(this.runtime, this.DEVICE_ID, originalDeviceId);
        this.bleUtils = new bleUtils();
    }

    getInfo() {
        return [{
            id: 'YMSWUSB',
            name: formatMessage({
                id: 'YMSWUSB.categoryName',
                default: 'YM SWUSB',
                description: 'Label for the YM SWUSB extension category'
            }),
            color1: "#4CAF50",
            color2: "#43A047",
            color3: "#388E3C",
            blocks: [
                {
                    opcode: 'connect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMSWUSB.ble.connect',
                        default: 'connect to YM-SWUSB-[DEVICE_NAME]',
                        description: 'Connect to YM SWUSB'
                    }),
                    arguments: {
                        DEVICE_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: ' ',
                        }
                    }
                },
                {
                    opcode: 'disConnect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMSWUSB.ble.disconnect',
                        default: 'disconnect from YM SWUSB',
                        description: 'Disconnect from YM SWUSB'
                    })
                },
                {
                    opcode: 'setUsbSwitch',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMSWUSB.setUsbSwitch',
                        default: 'turn Usb Power [SWITCH]',
                        description: 'Turn the USB POWER on or off'
                    }),
                    arguments: {
                        SWITCH: {
                            type: ArgumentType.NUMBER,
                            menu: 'lightSwitch'
                        }
                    }
                },
                {
                    opcode: 'setSwitch',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMSWUSB.setSwitch',
                        default: 'set duty cycle [DUTY_CYCLE]%',
                        description: 'Set switch duty cycle'
                    }),
                    arguments: {
                        DUTY_CYCLE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0,
                            min: 0,
                            max: 100
                        }
                    }
                }
            ],
            menus: {
                lightSwitch: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: 'YMLED.setLightSwitch.1',
                                default: 'On',
                                description: 'Light switch on'
                            }), value: '1'
                        },
                        {
                            text: formatMessage({
                                id: 'YMLED.setLightSwitch.0',
                                default: 'Off',
                                description: 'Light switch off'
                            }), value: '0'
                        }
                    ]
                },
            }
        }];
    }

    async connect(args) {
        const deviceName = 'YM-SWUSB-' + (args.DEVICE_NAME?.trim() || "");
        console.log("this.bleUtils.device swusb", this.bleUtils.device);
        const connected = await this.isConnected();
        if (connected) {
            console.log('Already connected to device');
            return;
        }
        console.log(`Connecting to YM SWUSB device: ${deviceName}`);
        try {
            await this.bleUtils.connect(deviceName, BLE_CONFIG.SERVICE_UUID);
            console.log(`Connected to device: ${deviceName}`);
        } catch (error) {
            console.error(`Failed to connect to ${deviceName}:`, error);
        }
    }

    async isConnected() {
        console.log('Checking if device is connected');
        return this.bleUtils.device !== null && this.bleUtils.device.gatt.connected;
    }

    async disConnect() {
        console.log('Disconnecting from YM SWUSB device');
        if (this.bleUtils.device) {
            try {
                await this.bleUtils.disConnect(this.bleUtils.device);
                console.log('Disconnected from device');
            } catch (error) {
                console.error('Failed to disconnect:', error);
            }
        }
    }
    async setUsbSwitch(args){
        const state = args.SWITCH;
        console.log(`Turning light ${state === '1' ? 'on' : 'off'}`);
        if (this.bleUtils.device) {
            try {
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.ledSwitch);
                const stateValue = parseInt(state, 10);
                if (isNaN(stateValue)) {
                    console.error('Invalid state value:', state);
                    return;
                }
                const stateArray = new Uint8Array(1);
                stateArray[0] = stateValue; 
                await this.bleUtils.writeCharacteristic(characteristic, stateArray);
            } catch (error) {
                console.error('Failed to set light switch:', error);
            }
        }

    }
    async setSwitch(args) {
        console.log('Setting switch duty cycle');
        let dutyCycle = Number(args.DUTY_CYCLE);
        if (isNaN(dutyCycle)) dutyCycle = 0;
        dutyCycle = Math.max(0, Math.min(100, Math.round(dutyCycle)));
        if (this.bleUtils.device) {
            try {
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.ledBrightness);
                const data = new Uint8Array([dutyCycle]);
                await this.bleUtils.writeCharacteristic(characteristic, data);
                console.log('Duty cycle set to', dutyCycle, '%');
            } catch (error) {
                console.error('Failed to set duty cycle:', error);
            }
        }
    }
}

module.exports = ScratchYMSWUSBDevice;
